import { apiService } from '../../../../components/api/apiService';
import { logDataModels } from '../dataModelsLogger';
import { fetchTablePreview } from '../tablePreviewApi';
import type { PolicyColumn } from './types';

type SchemaNode = {
  name?: string;
  Name?: string;
  type?: string;
  Type?: string;
  path?: string;
  Path?: string;
  dataType?: string;
  DataType?: string;
  children?: SchemaNode[];
  Children?: SchemaNode[];
  expandable?: boolean;
  Expandable?: boolean;
};

const ASSET_TYPES = new Set(['table', 'view', 'collection', 'file', 'object']);
const COLUMN_TYPES = new Set(['column', 'field', 'property', 'attribute']);
const MAX_EXPANDS = 24;
const cache = new Map<string, PolicyColumn[]>();
const inflight = new Map<string, Promise<PolicyColumn[]>>();

function cacheKey(sourceId: string, tableName: string, path?: string): string {
  return `${sourceId}:${path || tableName}`;
}

export async function loadTableColumns(sourceId: string, tableName: string, path?: string): Promise<PolicyColumn[]> {
  const key = cacheKey(sourceId, tableName, path);
  const hit = cache.get(key);
  if (hit?.length) return hit;
  const pending = inflight.get(key);
  if (pending) return pending;
  logDataModels('app', 'policy_columns_load', { sourceId, tableName, path: path || '' });
  const request = (async () => {
    try {
      const fromPreview = await columnsFromPreview(sourceId, tableName, path);
      if (fromPreview.length) {
        cache.set(key, fromPreview);
        logDataModels('app', 'policy_columns_load_ok', {
          sourceId,
          tableName,
          count: fromPreview.length,
          source: 'table_preview',
          names: fromPreview.slice(0, 8).map((column) => column.name),
        });
        return fromPreview;
      }
      const fromTree = path
        ? await columnsFromPath(sourceId, path)
        : await columnsFromName(sourceId, tableName);
      if (fromTree.length) cache.set(key, fromTree);
      logDataModels(fromTree.length ? 'app' : 'warning', fromTree.length ? 'policy_columns_load_ok' : 'policy_columns_load_empty', {
        sourceId,
        tableName,
        count: fromTree.length,
        source: 'datastore_tree',
        names: fromTree.slice(0, 8).map((column) => column.name),
      });
      return fromTree;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load columns';
      logDataModels('error', 'policy_columns_load_failed', { sourceId, tableName, message });
      throw new Error(message);
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, request);
  return request;
}

async function columnsFromPreview(sourceId: string, tableName: string, path?: string): Promise<PolicyColumn[]> {
  try {
    const preview = await fetchTablePreview({ sourceId, tableName, path, page: 1, pageSize: 25 });
    return preview.columns
      .filter((column) => column.name)
      .map((column) => ({
        name: column.name,
        type: column.type && column.type !== 'unknown' ? column.type : 'TEXT',
        tags: [],
        nullable: true,
      }));
  } catch (error) {
    logDataModels('warning', 'policy_columns_preview_failed', {
      sourceId,
      tableName,
      message: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}

async function columnsFromPath(sourceId: string, path: string): Promise<PolicyColumn[]> {
  const nodes = await expandPath(sourceId, path);
  const columns = columnsFrom(nodes);
  if (columns.length) return columns;
  return columnsFromNested(sourceId, nodes, path);
}

async function columnsFromName(sourceId: string, tableName: string): Promise<PolicyColumn[]> {
  const roots = await discoverNodes(sourceId);
  const match = await findAsset(sourceId, roots, tableName);
  if (!match) {
    logDataModels('warning', 'policy_columns_table_missing', { sourceId, tableName });
    return [];
  }
  const path = nodePath(match) || tableName;
  if (COLUMN_TYPES.has(nodeType(match))) return [toColumn(match)];
  const children = childNodes(match);
  const direct = columnsFrom(children);
  if (direct.length) return direct;
  if (!path) return [];
  return columnsFromPath(sourceId, path);
}

async function findAsset(sourceId: string, roots: SchemaNode[], tableName: string): Promise<SchemaNode | null> {
  const wanted = tableName.trim().toLowerCase();
  let calls = 0;
  const walk = async (nodes: SchemaNode[], depth: number): Promise<SchemaNode | null> => {
    if (depth > 3 || calls >= MAX_EXPANDS) return null;
    for (const node of nodes) {
      const name = nodeName(node).toLowerCase();
      const type = nodeType(node);
      if (name === wanted && (ASSET_TYPES.has(type) || !type)) return node;
      let children = childNodes(node);
      if (!children.length && isExpandable(node) && !ASSET_TYPES.has(type) && !COLUMN_TYPES.has(type)) {
        const path = nodePath(node);
        if (!path || calls >= MAX_EXPANDS) continue;
        calls += 1;
        children = await expandPath(sourceId, path);
        node.children = children;
      }
      const found = await walk(children, depth + 1);
      if (found) return found;
    }
    return null;
  };
  return walk(roots, 0);
}

async function columnsFromNested(sourceId: string, nodes: SchemaNode[], parentPath: string): Promise<PolicyColumn[]> {
  const found: PolicyColumn[] = [];
  for (const node of nodes) {
    const type = nodeType(node);
    if (COLUMN_TYPES.has(type) || (!type && !ASSET_TYPES.has(type) && !isExpandable(node))) {
      const column = toColumn(node);
      if (column.name) found.push(column);
      continue;
    }
    if (ASSET_TYPES.has(type)) continue;
    const path = nodePath(node) || (parentPath ? `${parentPath}.${nodeName(node)}` : nodeName(node));
    if (!path || !isExpandable(node)) continue;
    const children = await expandPath(sourceId, path);
    found.push(...columnsFrom(children));
  }
  return found;
}

async function discoverNodes(storeId: string): Promise<SchemaNode[]> {
  const data = await apiService.get(`/api/DataStores/${encodeURIComponent(storeId)}/discover`, {}, 'backend');
  return nodesFrom(data);
}

async function expandPath(storeId: string, path: string): Promise<SchemaNode[]> {
  try {
    const data = await apiService.get(
      `/api/DataStores/${encodeURIComponent(storeId)}/expand?path=${encodeURIComponent(path)}`,
      {},
      'backend',
    );
    return nodesFrom(data);
  } catch (error) {
    logDataModels('warning', 'policy_columns_expand_failed', {
      sourceId: storeId,
      path,
      message: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}

function columnsFrom(nodes: SchemaNode[]): PolicyColumn[] {
  const columns: PolicyColumn[] = [];
  const seen = new Set<string>();
  nodes.forEach((node) => {
    const type = nodeType(node);
    const name = nodeName(node);
    if (!name || ASSET_TYPES.has(type) || isContainer(type)) return;
    if (type && !COLUMN_TYPES.has(type) && childNodes(node).length) return;
    if (seen.has(name.toLowerCase())) return;
    seen.add(name.toLowerCase());
    columns.push(toColumn(node));
  });
  return columns;
}

function toColumn(node: SchemaNode): PolicyColumn {
  const dataType = String(node.dataType || node.DataType || '').trim();
  return {
    name: nodeName(node),
    type: dataType || nodeType(node) || 'TEXT',
    tags: [],
    nullable: true,
  };
}

function nodesFrom(data: unknown): SchemaNode[] {
  if (Array.isArray(data)) return data as SchemaNode[];
  if (!data || typeof data !== 'object') return [];
  const record = data as { nodes?: unknown; Nodes?: unknown };
  if (Array.isArray(record.nodes)) return record.nodes as SchemaNode[];
  if (Array.isArray(record.Nodes)) return record.Nodes as SchemaNode[];
  return [];
}

function childNodes(node: SchemaNode): SchemaNode[] {
  if (Array.isArray(node.children)) return node.children;
  if (Array.isArray(node.Children)) return node.Children;
  return [];
}

function nodeName(node: SchemaNode): string {
  return String(node.name || node.Name || '').trim();
}

function nodeType(node: SchemaNode): string {
  return String(node.type || node.Type || '').trim().toLowerCase();
}

function nodePath(node: SchemaNode): string {
  return String(node.path || node.Path || '').trim();
}

function isExpandable(node: SchemaNode): boolean {
  if (node.expandable || node.Expandable) return true;
  return isContainer(nodeType(node));
}

function isContainer(type: string): boolean {
  return type === 'folder' || type === 'bucket' || type === 'database' || type === 'schema' || type === 'dataset';
}
